import React, { useState, useEffect } from 'react';
import './SignupForm.css';

function SignupForm({ onSignupSuccess, showForm }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    plan: 'pro',
    timezone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    timezone: false
  });
  const [timezones, setTimezones] = useState([]);

  // Load timezones on component mount
  useEffect(() => {
    // Get user's local timezone as default
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Generate timezone list
    const timezoneList = generateTimezoneList();
    setTimezones(timezoneList);

    // Set default timezone
    setFormData(prevState => ({
      ...prevState,
      timezone: userTimezone
    }));
  }, []);

  // Generate list of timezones with UTC offsets
  const generateTimezoneList = () => {
    // List of common timezones with their UTC offsets
    return [
      { name: '(GMT-12:00) International Date Line West', value: 'Etc/GMT+12', offset: '-12' },
      { name: '(GMT-11:00) Midway Island, Samoa', value: 'Pacific/Midway', offset: '-11' },
      { name: '(GMT-10:00) Hawaii', value: 'Pacific/Honolulu', offset: '-10' },
      { name: '(GMT-09:00) Alaska', value: 'America/Anchorage', offset: '-9' },
      { name: '(GMT-08:00) Pacific Time (US & Canada)', value: 'America/Los_Angeles', offset: '-8' },
      { name: '(GMT-07:00) Arizona', value: 'Arizona', offset: '-7' },
      { name: '(GMT-07:00) Mountain Time (US & Canada)', value: 'America/Denver', offset: '-7' },
      { name: '(GMT-06:00) Central Time (US & Canada)', value: 'America/Chicago', offset: '-6' },
      { name: '(GMT-05:00) Eastern Time (US & Canada)', value: 'America/New_York', offset: '-5' },
      { name: '(GMT-04:00) Atlantic Time (Canada)', value: 'America/Halifax', offset: '-4' },
      { name: '(GMT-03:00) Brasilia, Buenos Aires', value: 'America/Sao_Paulo', offset: '-3' },
      { name: '(GMT-02:00) Mid-Atlantic', value: 'Atlantic/South_Georgia', offset: '-2' },
      { name: '(GMT-01:00) Azores', value: 'Atlantic/Azores', offset: '-1' },
      { name: '(GMT+00:00) London, Dublin, Edinburgh', value: 'Europe/London', offset: '0' },
      { name: '(GMT+01:00) Paris, Berlin, Rome, Madrid', value: 'Europe/Paris', offset: '1' },
      { name: '(GMT+02:00) Athens, Istanbul, Helsinki', value: 'Europe/Athens', offset: '2' },
      { name: '(GMT+03:00) Moscow, Baghdad, Kuwait', value: 'Europe/Moscow', offset: '3' },
      { name: '(GMT+04:00) Abu Dhabi, Dubai, Baku', value: 'Asia/Dubai', offset: '4' },
      { name: '(GMT+05:00) Karachi, Tashkent', value: 'Asia/Karachi', offset: '5' },
      { name: '(GMT+05:30) Kolkata, Chennai, Mumbai, New Delhi', value: 'Asia/Kolkata', offset: '5.5' },
      { name: '(GMT+06:00) Dhaka, Almaty', value: 'Asia/Dhaka', offset: '6' },
      { name: '(GMT+07:00) Bangkok, Hanoi, Jakarta', value: 'Asia/Bangkok', offset: '7' },
      { name: '(GMT+08:00) Beijing, Hong Kong, Singapore', value: 'Asia/Shanghai', offset: '8' },
      { name: '(GMT+09:00) Tokyo, Seoul', value: 'Asia/Tokyo', offset: '9' },
      { name: '(GMT+10:00) Sydney, Melbourne, Brisbane', value: 'Australia/Sydney', offset: '10' },
      { name: '(GMT+11:00) Solomon Islands', value: 'Pacific/Guadalcanal', offset: '11' },
      { name: '(GMT+12:00) Auckland, Wellington', value: 'Pacific/Auckland', offset: '12' }
    ];
  };

  // Helper function to get UTC offset from selected timezone
  const getUtcOffsetFromTimezone = (selectedTimezone) => {
    const found = timezones.find(tz => tz.value === selectedTimezone);
    return found ? found.value : '0';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // Clear error for the field being edited
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: false
      });

      // Clear general error message if we're editing a field that had an error
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Reset all errors first
    setError('');
    setFieldErrors({
      firstName: false,
      lastName: false,
      email: false,
      timezone: false
    });

    // Individual field validation
    let hasErrors = false;
    let newFieldErrors = {...fieldErrors};

    // Check first name
    if (!formData.firstName.trim()) {
      newFieldErrors.firstName = true;
      hasErrors = true;
    }

    // Check last name
    if (!formData.lastName.trim()) {
      newFieldErrors.lastName = true;
      hasErrors = true;
    }

    // Check email
    if (!formData.email.trim()) {
      newFieldErrors.email = true;
      setError('Email address is required');
      hasErrors = true;
    } else {
      // Simple email validation on client side
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newFieldErrors.email = true;
        setError('Please enter a valid email address');
        hasErrors = true;
      }
    }

    // Check timezone
    if (!formData.timezone.trim()) {
      newFieldErrors.timezone = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setLoading(false);
      return;
    }

    try {
      // Get UTC offset from the selected timezone
      const utcOffset = getUtcOffsetFromTimezone(formData.timezone);

      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          utcOffset: utcOffset
        })
      });

      // Parse response
      let data;
      try {
        const responseText = await response.text();
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Server response was not valid JSON');
      }

      if (!data.success) {
        // Handle field-specific errors
        if (data.field) {
          setFieldErrors({
            ...fieldErrors,
            [data.field]: true
          });
        }

        setError(data.message || 'An error occurred during signup');
        setLoading(false);
        return;
      }

      // Handle successful signup
      if (data.success && formData.plan === 'pro') {
        // For paid plan signup
        window.location.href = data.stripeUrl;
      } else if (data.success) {
        // For free plan signup
        window.location.href = `/sign-up-success?email=${encodeURIComponent(formData.email)}`;
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'An unexpected error occurred');
      setFieldErrors({
        ...fieldErrors,
        email: true // Highlight email field for general errors
      });
      setLoading(false);
    }
  };

  if (!showForm) return null;

  return (
    <div className="">
      <form onSubmit={handleSubmit} className="" noValidate>
        <div className="pt-1 field">
          <label className="label">First name
          {fieldErrors.firstName && (
            <span className="help colorDanger mb-1">First name is required</span>
          )}
          </label>
          <div className="control">
            <input
              className={`input ${fieldErrors.firstName ? 'colorDanger error' : ''}`}
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              style={{
                color: fieldErrors.firstName ? '#ff0000' : 'inherit'
              }}
              required
            />
          </div>
        </div>

        <div className="pt-1 field">
          <label className="label">Last name
          {fieldErrors.lastName && (
            <span className="help colorDanger mb-1">Last name is required</span>
          )}
          </label>
          <div className="control">
            <input
              className={`input ${fieldErrors.lastName ? 'colorDanger error' : ''}`}
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              style={{
                color: fieldErrors.lastName ? '#ff0000' : 'inherit'
              }}
              required
            />
          </div>
        </div>

        <div className="pt-1 field">
          <label className="label">Email address
          {fieldErrors.email && (
            <span className="help colorDanger mb-1" dangerouslySetInnerHTML={{
              __html: error || 'Email address is required'
            }} />
          )}
          </label>
          <div className="control">
            <input
              className={`input ${fieldErrors.email ? 'colorDanger error' : ''}`}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                color: fieldErrors.email ? '#ff0000' : 'inherit'
              }}
              required
            />
          </div>
        </div>

        <div className="pt-1 field">
          <label className="label">Your timezone
          {fieldErrors.timezone && (
            <span className="help colorDanger mb-1">Timezone is required</span>
          )}
          </label>
          <div className="control">
            <div className="select is-fullwidth">
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className={fieldErrors.timezone ? 'colorDanger error' : ''}
                style={{
                  color: fieldErrors.timezone ? '#ff0000' : 'inherit'
                }}
                required
              >
                <option value="">Select your timezone</option>
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-1 field">
          <label className="label">Select your Nudgemail plan</label>
          <div className="control">
            <div className="select is-fullwidth">
              <select
                name="plan"
                value={formData.plan}
                onChange={handleChange}
                required
              >
                <option value="pro">Monthly Heavyweight hero - $3/mo</option>
                <option value="pro_yearly">Yearly Heavyweight hero - $36/year</option>
                <option value="free">Lightweight legend - Free</option>
              </select>
            </div>
          </div>
        </div>

        <div className="pt-5 field">
          <div className="control">
            <button
              className={`button is-primary is-fullwidth ${loading ? 'is-loading' : ''}`}
              type="submit"
              disabled={loading}
            >
              Sign up
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SignupForm;
